import { Box, SxProps, Theme } from '@mui/material';
import React, { ReactNode, useRef, useState } from 'react';
import fadeInSectionStyles from './fadeInSection.module.scss';
import { useScrollDirection } from 'src/hooks/useScrollDirection';

enum Direction {
  TOP = 'top',
  RIGHT = 'right',
  BOTTOM = 'bottom',
  LEFT = 'left',
  FRONT = 'front',
}

export const FadeInSection: React.FC<{
  delay?: number;
  fromDirection?: 'top' | 'right' | 'bottom' | 'left' | 'front';
  single?: boolean; // if true, it means just do the animation once. if false, it means to reset the element so that when the viewport and element intersect again, the animation will happen again.
  scrollBased?: boolean; // if true, it means it will flip the fromDirection of the animation if user is scrolling up. it will keep the fromDirection as intended if the scroll direction is down. if false, the animation is always done the way as passed in by fromDirection
  styles?: React.CSSProperties;
  children: ReactNode;
}> = ({
  delay = 0,
  fromDirection = Direction.BOTTOM,
  single = false,
  scrollBased = true,
  styles,
  children,
}) => {
  const [isVisible, setVisible] = useState(false);
  const scrollDirection = scrollBased ? useScrollDirection() : 'down';
  const domRef = useRef<HTMLDivElement>(null);
  // learned from https://blog.webdevsimplified.com/2022-01/intersection-observer/
  // https://wesbos.com/javascript/06-serious-practice-exercises/scroll-events-and-intersection-observer
  // NOTE: intersection observer is a faster and more efficient way than "getboundingclientrect"
  React.useEffect(() => {
    if (domRef === null || domRef.current === null) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisible(true);
            }, delay);
          } else {
            if (!single) {
              setVisible(false);
            }
          }
        });
      },
      {
        rootMargin: '32px', // artificially expands the intersection area of the viewport. this helps prevent the apparent lag that happens when scrolling because the element animation has a good amount of head start before the whole element is in the actual viewport. before, it seemed like there was a lag bc the animation only started when the element was fully in the viewport. (another solution would've been to make the threshold: 0.8 or some fraction of one)
      },
    );
    observer.observe(domRef.current);

    console.log(delay);
    return () => {
      if (domRef === null || domRef.current === null) {
        return;
      }
      observer.unobserve(domRef.current);
    };
  }, []);

  const getInitialTranslation = () => {
    // TODO: if the element is intersecting into the viewport from the top instead of from the bottom, then flip the numbers. (i.e. -10vh -> 10vh, 10vw -> -10vw, etc.)
    //      then uncomment the part that controls making the isVisible state false when the element is no longer intersecting with the viewport
    // another solution would be to do it based on scroll direction (normal if scrolling from top but flipped if scrolling from bottom)
    return fromDirection === Direction.TOP
      ? `translateY(${scrollDirection === 'down' ? '-10vh' : '10vh'})`
      : fromDirection === Direction.RIGHT
      ? `translateX(${scrollDirection === 'down' ? '10vw' : '-10vw'})`
      : fromDirection === Direction.BOTTOM
      ? `translateY(${scrollDirection === 'down' ? '10vh' : '-10vh'})`
      : fromDirection === Direction.LEFT
      ? `translateX(${scrollDirection === 'down' ? '-10vw' : '10vw'})`
      : 'none';
  };

  return (
    <div
      // className={`fade-in-section ${isVisible ? 'is-visible' : ''}`}
      style={{
        ...styles,
        // transitionDelay: `100s`, // NOTE: for some reason the transition delay would not work so I had to set a timeout above to delay when the isVisible state is changed
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? 'none'
          : fromDirection === Direction.TOP ||
            fromDirection === Direction.RIGHT ||
            fromDirection === Direction.BOTTOM ||
            fromDirection === Direction.LEFT
          ? getInitialTranslation()
          : 'none',
        // NOTE: an alternate way of doing translate 'x-axis y-axis' (starting from top left corner as 0. down direction is positive y. right direction is positive x)
        // translate: '10vh 0',
        // NOTE: in the types, it says it accepts a number, but for some reason, it only accepts a string when used in jsx style. when I used a number, the program did not recognize the scale property
        scale: isVisible ? '1' : fromDirection === Direction.FRONT ? '2' : '1',
        // visibility: isVisible ? 'visible' : 'hidden',
        transition:
          'opacity 0.5s ease-out, transform 0.8s ease-out, scale 0.8s ease-out',
        // willChange: 'opacity, visibility',
      }}
      ref={domRef}
    >
      {children}
    </div>
  );
};
